import React, { useState } from 'react';
import { Modal, Box, Typography, TextField, Button, Popover, IconButton, CircularProgress, MenuItem, Select } from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useTranslation } from 'react-i18next';
import OTPInput from 'react-otp-input';
import { MOBILE_START_WITH_05 } from '../../../../utils/regex';
import { useProperty } from '@services';
import { useIsMutating } from '@tanstack/react-query';
import { ToastMessageContext } from '@context';
import { useContext } from 'react';
import  DisconnectedIllustration from '@assets/svg/unconnect.svg?react';

interface GathernTokenExpiredModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const GathernTokenExpiredModal: React.FC<GathernTokenExpiredModalProps> = ({ 
    open, 
    onClose,
    onSuccess 
}) => {
    const { t } = useTranslation();
    const mutating = useIsMutating();
    const [step, setStep] = useState<'phone' | 'otp' | 'success'>('phone');
    const [phone, setPhone] = useState('');
    const [otp, setOtp] = useState('');
    const { showError } = useContext(ToastMessageContext);
    const { gathrenLoginMutate, gathrenVerifyOtpMutate } = useProperty();

    const { mutate: gathrenVerifyOtp } = gathrenVerifyOtpMutate({
        onError: () => {
            showError(t('ERRORS.SOMETHING_WENT_WRONG'));
        },
        onSuccess: () => {
            setStep('success');
        },
    });

    const { mutate: gathrenLogin } = gathrenLoginMutate({
        onError: () => {
            showError(t('ERRORS.SOMETHING_WENT_WRONG'));
        },
        onSuccess: () => {
            setStep('otp');
        },
    });

    const handleClose = () => {
        setStep('phone');
        setPhone('');
        setOtp('');
        onClose();
    };

    const handleSuccessClose = () => {
        onSuccess();
        handleClose();
    };

    const sendOtp = () => {
        if (new RegExp(MOBILE_START_WITH_05).test(phone)) {
            const payload: any = {
                phone: phone,
            };
            gathrenLogin(payload);
        } else {
            showError(t('ERRORS.INVALID_MOBILE'));
        }
    };

    const onOtpSubmit = () => {
        const payload: any = {
            phone: phone,
            otp: otp,
        };
        gathrenVerifyOtp(payload);
    };

    return (
        <Modal 
            open={open} 
            onClose={(event, reason) => {
                // Prevent closing on backdrop click or escape key
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    return;
                }
                handleClose();
            }}
        >
            <div className="flex items-center justify-center h-screen">
                <div className="w-[900px] min-h-[700px] rounded-3xl bg-white relative shadow-2xl flex  overflow-hidden">
                    {/* Left side - Illustration */}
                    {step === 'phone' || step === 'otp' ? <div className="absolute left-0 top-0 bottom-0 w-[45%] flex items-center justify-center">
                        <DisconnectedIllustration className="w-full h-full" />
                    </div> : null}

                    {/* Right side - Content */}
                    <div className={`${step === 'phone' || step === 'otp' ? 'w-[55%] justify-start flex-col items-start pr-[64px] pt-[86px] pl-[24px] pb-[48px]' : 'w-full justify-center flex-col items-center'} ml-auto flex `}>
                        {/* Phone Step */}
                        {step === 'phone' && (
                            <>
                                {/* Warning Icon */}
                                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-6">
                                    <WarningAmberRoundedIcon className="text-red-600" style={{ fontSize: '36px' }} />
                                </div>

                                <h2 className="text-start font-bold text-2xl leading-7 text-[#331860] mb-4">
                                    {t('PROPERTIES_MANAGEMENT.GATHERN_TOKEN_EXPIRED')}
                                </h2>
                                <p className="text-start text-[#625783] mb-16 font-normal text-sm leading-5 max-w-[400px]">
                                    {t('PROPERTIES_MANAGEMENT.GATHERN_TOKEN_EXPIRED_DESCRIPTION')}
                                </p>
                                <div className="flex flex-col gap-16 items-center w-full max-w-[400px]">
                                    {/* Phone Input with Country Code */}
                                    <div className="w-full flex flex-col gap-6">
                                        <label className="text-sm font-medium text-[#331860]">
                                            {t('PROPERTIES_MANAGEMENT.PHONE_INPUT_LABEL')}
                                        </label>
                                        <TextField
                                            id='phone'
                                            InputProps={{ 
                                                sx: { 
                                                    borderRadius: '8px',
                                                    '& input': {
                                                        textAlign: 'left',
                                                        direction: 'ltr'
                                                    }
                                                } 
                                            }}
                                            className="w-full"
                                            placeholder={t('PROPERTIES_MANAGEMENT.PHONE_PLACEHOLDER') || '50 xxx xxxx'}
                                            value={phone}
                                            variant='outlined'
                                            onChange={(e) => setPhone(e.target.value)}
                                        />
                                    </div>
                                    <Button 
                                        variant='contained' 
                                        onClick={sendOtp} 
                                        disabled={phone?.length != 10 || mutating > 0}
                                        className="!px-24 rounded-lg bg-[#5B3FFF] hover:bg-[#4a32cc] disabled:bg-gray-300 disabled:text-gray-500 text-base font-semibold normal-case"
                                        startIcon={mutating > 0 ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null}
                                    >
                                        {mutating > 0 ? t('COMMON.LOADING') : t('PROPERTIES_MANAGEMENT.NEXT')}
                                    </Button>
                                </div>
                            </>
                        )}

                        {/* OTP Step */}
                        {step === 'otp' && (
                            <>
                                <h2 className="text-center font-bold text-2xl leading-7 text-[#331860] mb-4">
                                    {t('PROPERTIES_MANAGEMENT.ENTER_OTP_CODE')}
                                </h2>
                                <p className="text-center text-[#625783] mb-8 font-normal text-sm leading-5 max-w-[400px]">
                                    {t('PROPERTIES_MANAGEMENT.OTP_SENT_TO_PHONE')}
                                </p>
                                <div className="flex flex-col gap-64 items-center w-full max-w-[400px]">
                                    <div className="w-full otp-input-container" style={{ direction: 'ltr' }}>
                                        <style>{`
                                            .otp-input-container input {
                                                width: 48px;
                                                height: 56px;
                                                border: none;
                                                border-bottom: 2px solid #9CA3AF;
                                                background: transparent;
                                                text-align: center;
                                                font-size: 24px;
                                                font-weight: 600;
                                                color: #331860;
                                                transition: all 0.2s ease;
                                                outline: none;
                                            }
                                            .otp-input-container input:focus {
                                                border-bottom-color: #5B3FFF;
                                                color: #5B3FFF;
                                            }
                                            .otp-input-container input:not(:placeholder-shown) {
                                                color: #331860;
                                            }
                                        `}</style>
                                        <OTPInput
                                            value={otp}
                                            containerStyle={'gap-4 mt-2 justify-center'}
                                            shouldAutoFocus={true}
                                            inputType={'tel'}
                                            skipDefaultStyles={true}
                                            onChange={setOtp}
                                            numInputs={4}
                                            renderInput={(props) => <input {...props} />}
                                        />
                                    </div>
                                    <Button
                                        variant='contained'
                                        onClick={onOtpSubmit}
                                        disabled={otp?.length != 4 || mutating > 0}
                                        className="!px-24 rounded-lg bg-[#5B3FFF] hover:bg-[#4a32cc] disabled:bg-gray-300 disabled:text-gray-500 text-base font-semibold normal-case"
                                        startIcon={mutating > 0 ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null}
                                    >
                                        {mutating > 0 ? t('COMMON.LOADING') : t('PROPERTIES_MANAGEMENT.NEXT')}
                                    </Button>
                                </div>
                            </>
                        )}

                        {/* Success Step */}
                        {step === 'success' && (
                            <>
                                {/* Success Icon */}
                                <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center mb-6">
                                    <CheckCircleOutlineIcon className="text-green-600" style={{ fontSize: '56px' }} />
                                </div>

                                <h2 className="text-center font-bold text-2xl leading-7 text-[#331860] mb-4">
                                    {t('PROPERTIES_MANAGEMENT.LINK_RESTORED_SUCCESS')}
                                </h2>
                                <p className="text-center text-[#625783] mb-8 font-normal text-sm leading-5 max-w-[400px]">
                                    {t('PROPERTIES_MANAGEMENT.ENJOY_MSOOL_SERVICES')}
                                </p>
                                <div className="flex flex-col gap-6 items-center w-full max-w-[400px]">
                                    <Button
                                        variant='contained'
                                        onClick={handleSuccessClose}
                                        className="w-full h-12 rounded-lg bg-[#5B3FFF] hover:bg-[#4a32cc] text-base font-semibold normal-case"
                                    >
                                        {t('PROPERTIES_MANAGEMENT.OK')}
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Close button removed - modal can only be closed on successful OTP verification */}
                </div>
            </div>
        </Modal>
    );
};